import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, BadgeCheck, Check, Clock, MapPin } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MaskLines, Reveal } from "@/components/site/Reveal";
import { TransportFields } from "@/components/site/TransportFields";
import { SessionList } from "@/components/site/SessionList";

const EnrolBox = ({ course, sessions }) => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [note, setNote] = useState("");
  const [transport, setTransport] = useState({ needs_transport: false, transport_for: course.category === "Seniors" ? "Myself" : "", pickup_suburb: "", transport_notes: "", session_id: "" });
  const [busy, setBusy] = useState(false);
  const [enrolment, setEnrolment] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/enrolments/me").then((r) => {
      const list = Array.isArray(r.data) ? r.data : [];
      setEnrolment(list.find((e) => e.course_slug === course.slug) || null);
    }).catch(() => {});
  }, [user, course.slug]);

  const submit = async () => {
    if (!user) { nav(`/login?next=/programmes/${course.slug}`); return; }
    if (transport.needs_transport && (!transport.transport_for || !transport.pickup_suburb.trim())) {
      toast.error("Please choose who needs a lift and add your pick-up suburb");
      return;
    }
    setBusy(true);
    try {
      const payload = transport.needs_transport ? transport : { needs_transport: false };
      const { data } = await api.post("/enrolments", { course_slug: course.slug, note, ...payload });
      setEnrolment(data);
      toast.success(transport.needs_transport ? "You're enrolled! We'll be in touch about your lift." : "You're enrolled! Our team will be in touch.");
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };

  if (enrolment) {
    return (
      <div data-testid="enrolment-status" className="rounded-3xl bg-gum p-8 text-sand">
        <Check className="h-8 w-8" />
        <p className="mt-4 font-display text-3xl">You're enrolled.</p>
        <p className="mt-2 text-sm text-sand/85">Status: <span className="font-mono uppercase">{enrolment.status}</span></p>
        {enrolment.needs_transport && <p data-testid="enrolment-transport" className="mt-1 text-sm text-sand/85">Lift: <span className="font-mono uppercase">{(enrolment.transport_status || "requested").replace("_", " ")}</span> · {enrolment.pickup_suburb}{enrolment.session_label ? ` · ${enrolment.session_label}` : ""}</p>}
        <Link to="/dashboard" data-testid="go-dashboard-link" className="mt-6 inline-block text-sm underline underline-offset-4">View my dashboard</Link>
      </div>
    );
  }
  return (
    <div className="rounded-3xl bg-ink p-8 text-sand">
      <p className="eyebrow text-amber">Enrol</p>
      <p className="mt-4 font-display text-3xl">Ready to start?</p>
      {user && (
        <>
          <TransportFields value={transport} onChange={setTransport} sessions={sessions} />
          <textarea data-testid="enrol-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything we should know? (optional)" rows={3}
            className="mt-4 w-full resize-none rounded-xl border border-sand/20 bg-transparent p-4 text-sm text-sand placeholder:text-sand/60 focus:border-ochre focus:outline-none" />
        </>
      )}
      <button data-testid="enrol-now-button" disabled={busy} onClick={submit} className="btn-ochre mt-6 w-full justify-center disabled:opacity-60">
        {busy ? "Enrolling…" : user ? "Enrol now" : "Log in to enrol"}
      </button>
    </div>
  );
};

export default function CourseDetail() {
  const { slug } = useParams();
  const [course, setCourse] = useState(undefined);
  const [sessions, setSessions] = useState([]);
  useEffect(() => {
    api.get(`/courses/${slug}`).then((r) => setCourse(r.data)).catch(() => setCourse(null));
    api.get(`/courses/${slug}/sessions`).then((r) => setSessions(Array.isArray(r.data) ? r.data : [])).catch(() => setSessions([]));
  }, [slug]);

  if (course === undefined) return <main className="min-h-screen bg-ink" />;
  if (!course) return <main className="flex min-h-screen items-center justify-center bg-sand pt-20"><p data-testid="course-not-found">Course not found. <Link to="/programmes" className="underline">Back to programmes</Link></p></main>;

  return (
    <main className="bg-sand">
      <section className="relative overflow-hidden bg-ink px-5 pb-16 pt-36 text-sand sm:px-10">
        <div className="mx-auto grid max-w-[1440px] gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Link to="/programmes" data-testid="back-to-programmes" className="inline-flex items-center gap-2 text-sm text-sand/80 hover:text-ochre"><ArrowLeft className="h-4 w-4" /> All programmes</Link>
            <p className="eyebrow mt-10 text-amber">{course.category}</p>
            {course.provider && <p data-testid="course-rto-badge" className="mt-4 inline-flex items-center gap-2 rounded-full border border-sand/30 px-4 py-1.5 text-xs"><BadgeCheck className="h-4 w-4 text-ochre" /> {course.provider}</p>}
            <MaskLines lines={[course.title]} className="mt-5 font-display text-5xl font-medium leading-[0.95] tracking-tight sm:text-7xl" />
            <div className="mt-8 flex flex-wrap gap-6 text-sm text-sand/85">
              <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-ochre" />{course.duration}</span>
              <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-ochre" />{course.mode}</span>
            </div>
          </div>
          <motion.div initial={{ clipPath: "inset(0 0 100% 0 round 32px)" }} animate={{ clipPath: "inset(0 0 0% 0 round 32px)" }} transition={{ duration: 1.3, ease: [0.76, 0, 0.24, 1] }} className="h-72 overflow-hidden lg:col-span-5 lg:h-[420px]">
            <img src={course.image} alt={course.title} className="h-full w-full object-cover" />
          </motion.div>
        </div>
      </section>
      <section className="px-5 py-20 sm:px-10">
        <div className="mx-auto grid max-w-[1440px] gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <p className="font-display text-3xl leading-snug text-ink">{course.summary}</p>
            <p className="mt-6 text-base leading-relaxed text-ink/80">{course.description}</p>
            <p className="eyebrow mt-14 text-ochre">What you'll cover</p>
            <ul className="mt-6">
              {(course.units || []).map((u, i) => (
                <li key={u} className="flex gap-6 border-t border-ink/15 py-5"><span className="font-mono text-xs text-ink/60">0{i + 1}</span><span>{u}</span></li>
              ))}
            </ul>
            <SessionList sessions={sessions} />
          </Reveal>
          <div className="lg:col-span-4 lg:col-start-9"><div className="lg:sticky lg:top-28"><EnrolBox course={course} sessions={sessions} /></div></div>
        </div>
      </section>
    </main>
  );
}
